import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import type { TablePaginationActionsProps } from '@mui/material/TablePaginationActions';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

export function TablePageNavigation({
  page,
  pageCount,
  onPageChange,
  disabled = false,
  className,
  previousLabel,
  nextLabel,
}: {
  page: number;
  pageCount: number | null;
  onPageChange: (page: number) => void;
  disabled?: boolean | undefined;
  className?: string | undefined;
  previousLabel?: string | undefined;
  nextLabel?: string | undefined;
}) {
  const { t } = useTranslation();
  const rtl = useTheme().direction === 'rtl';
  const Previous = rtl ? ChevronRightIcon : ChevronLeftIcon;
  const Next = rtl ? ChevronLeftIcon : ChevronRightIcon;
  const buttonSx = { borderRadius: '6px', bgcolor: 'action.hover', width: 32, height: 32 };
  return (
    <Stack direction="row" className={className} sx={{ alignItems: 'center', gap: 0.5 }}>
      <IconButton
        aria-label={previousLabel ?? t('common.previousPage')}
        disabled={disabled || page <= 1}
        onClick={() => onPageChange(page - 1)}
        sx={buttonSx}
      >
        <Previous width={16} height={16} />
      </IconButton>
      <Typography
        component={Box}
        aria-live="polite"
        aria-label={
          pageCount === null ? String(page) : t('common.pageOf', { page, total: pageCount })
        }
        sx={{
          fontSize: 13,
          fontWeight: 600,
          minWidth: 48,
          px: 1,
          textAlign: 'center',
          whiteSpace: 'nowrap',
        }}
      >
        <bdi>{pageCount === null ? page : `${page} / ${pageCount}`}</bdi>
      </Typography>
      <IconButton
        aria-label={nextLabel ?? t('common.nextPage')}
        disabled={disabled || (pageCount !== null && page >= pageCount)}
        onClick={() => onPageChange(page + 1)}
        sx={buttonSx}
      >
        <Next width={16} height={16} />
      </IconButton>
    </Stack>
  );
}

// Keep MUI/DataGrid's existing pagination model and server callbacks; only replace its view.
export function TablePaginationControls({
  count,
  page,
  rowsPerPage,
  onPageChange,
  disabled,
  className,
  getItemAriaLabel,
}: TablePaginationActionsProps) {
  return (
    <TablePageNavigation
      className={className}
      page={page + 1}
      pageCount={count < 0 ? null : Math.max(1, Math.ceil(count / rowsPerPage))}
      onPageChange={(next) => onPageChange(null, next - 1)}
      disabled={disabled}
      previousLabel={getItemAriaLabel('previous')}
      nextLabel={getItemAriaLabel('next')}
    />
  );
}
